'use client';

import React, { useMemo } from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import type { VerifySubscriptionPaymentPhase } from '@/features/payments/presentation/composables/hooks/useVerifySubscriptionPayment';
import {
  VERIFY_SUBSCRIPTION_FAILURE_BODY,
  VERIFY_SUBSCRIPTION_FAILURE_TITLE,
  VERIFY_SUBSCRIPTION_INVALID_ID_BODY,
  VERIFY_SUBSCRIPTION_LOADING,
  VERIFY_SUBSCRIPTION_NOT_FOUND_BODY,
  VERIFY_SUBSCRIPTION_PROCESSING_BODY,
  VERIFY_SUBSCRIPTION_PROCESSING_TITLE,
  VERIFY_SUBSCRIPTION_SUCCESS_BODY,
  VERIFY_SUBSCRIPTION_SUCCESS_TITLE,
} from '@/features/payments/presentation/constants/verify-subscription-copy';

export type VerifySubscriptionViewProps = {
  authBlocking: boolean;
  phase: VerifySubscriptionPaymentPhase;
  detailMessage: string | null;
};

type PanelTone = 'pending' | 'success' | 'error';

function panelTone(phase: VerifySubscriptionPaymentPhase, authBlocking: boolean): PanelTone {
  if (authBlocking || phase === 'listening' || phase === 'unauthenticated') return 'pending';
  if (phase === 'success') return 'success';
  return 'error';
}

function statusLabel(phase: VerifySubscriptionPaymentPhase, authBlocking: boolean): string {
  if (authBlocking) return 'Carregando';
  if (phase === 'listening' || phase === 'unauthenticated') return 'Processando';
  if (phase === 'success') return 'Concluído';
  return 'Atenção';
}

export const VerifySubscriptionView: React.FC<VerifySubscriptionViewProps> = ({
  authBlocking,
  phase,
  detailMessage,
}) => {
  const tone = useMemo(() => panelTone(phase, authBlocking), [phase, authBlocking]);
  const label = useMemo(() => statusLabel(phase, authBlocking), [phase, authBlocking]);

  const borderClass =
    tone === 'success' ? 'border-emerald-400/50'
    : tone === 'error' ? 'border-amber-400/45'
    : 'border-cyan/40';

  const icon =
    tone === 'success' ?
      <CheckCircle2 className="h-10 w-10 shrink-0 text-emerald-300" aria-hidden />
    : tone === 'error' ?
      <AlertCircle className="h-10 w-10 shrink-0 text-amber-300" aria-hidden />
    : <Loader2 className="h-10 w-10 shrink-0 text-cyan animate-spin" aria-hidden />;

  const titleAndBody = useMemo(() => {
    if (authBlocking) {
      return { title: VERIFY_SUBSCRIPTION_LOADING, body: null as string | null };
    }
    switch (phase) {
      case 'listening':
      case 'unauthenticated':
        return {
          title: VERIFY_SUBSCRIPTION_PROCESSING_TITLE,
          body: VERIFY_SUBSCRIPTION_PROCESSING_BODY,
        };
      case 'success':
        return {
          title: VERIFY_SUBSCRIPTION_SUCCESS_TITLE,
          body: VERIFY_SUBSCRIPTION_SUCCESS_BODY,
        };
      case 'invalid_payment_id':
        return {
          title: VERIFY_SUBSCRIPTION_FAILURE_TITLE,
          body: VERIFY_SUBSCRIPTION_INVALID_ID_BODY,
        };
      case 'not_found':
        return {
          title: VERIFY_SUBSCRIPTION_FAILURE_TITLE,
          body: VERIFY_SUBSCRIPTION_NOT_FOUND_BODY,
        };
      case 'payment_failed':
        return {
          title: VERIFY_SUBSCRIPTION_FAILURE_TITLE,
          body: VERIFY_SUBSCRIPTION_FAILURE_BODY,
        };
      case 'listen_error':
        return {
          title: VERIFY_SUBSCRIPTION_FAILURE_TITLE,
          body:
            detailMessage != null && detailMessage.trim().length > 0 ?
              detailMessage.trim()
            : VERIFY_SUBSCRIPTION_FAILURE_BODY,
        };
    }
  }, [authBlocking, phase, detailMessage]);

  const showDetail =
    !authBlocking &&
    phase === 'payment_failed' &&
    detailMessage != null &&
    detailMessage.trim().length > 0;

  return (
    <div
      className={`rounded-xl border px-5 py-6 sm:px-6 space-y-4 bg-white/10 ${borderClass}`}
    >
      <div className="flex items-start gap-3">
        {icon}
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-white/70">{label}</p>
          <h2 className="text-xl font-semibold text-white leading-snug">
            {titleAndBody.title}
          </h2>
          {titleAndBody.body ?
            <p className="text-sm text-white/85 leading-relaxed">{titleAndBody.body}</p>
          : null}
          {showDetail ?
            <p className="text-sm text-white/75 leading-relaxed border-t border-white/10 pt-3 mt-1">
              {detailMessage}
            </p>
          : null}
        </div>
      </div>
    </div>
  );
};
